import React, { useContext } from "react";

import { DispatchContext } from "../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../context/global/WeeklyContext";
import { LockClosed } from "../../../components/icons/svg/outline";

import { formatDate } from "../../../utils/datetime";

/**
 * Legacy: absolute overlays the capture grid; stacked sits in normal flow under TopBar (full-page sticky).
 * Week strip header for the daily dispatch grid: weekday labels, dates, and a
 * lock icon when that day is dispatch-locked. Reads visible week days from
 * {@link WeeklyContext} and lock state from {@link DispatchContext}.
 *
 * @param {Object} props - Component props.
 * @param {"absolute"|"stacked"} [props.layout="absolute"] Absolute overlays the
 *   grid (legacy/capture); stacked sits in normal flow under TopBar (full-page sticky).
 * @returns {JSX.Element}
 */
const DatesBar = ({ layout = "absolute" }) => {
  const { showWeekends, datesOfWeek } = useContext(WeeklyContext);
  const dispatchContext = useContext(DispatchContext);
  const dayDetails = dispatchContext?.dayDetails ?? null;

  const datesOfWeekStrs = datesOfWeek.map((date) =>
    formatDate(date, "MM-DD-YY")
  );

  const gridCols = showWeekends ? "grid-cols-7" : "grid-cols-5";
  // Cells fill the track so border-r sits on the column edge — the center of
  // the 8px gap between SummaryBar/Job day cards (m-1+m-1 or gap-2).
  const dateCells = datesOfWeek.map((date, index) => {
    const lockedForDay = Boolean(
      dayDetails?.find(
        (dayRow) => dayRow.date === formatDate(date, "YYYY-MM-DD")
      )?.locked
    );
    return (
      <div
        key={index}
        className="my-2 border-r border-gray-300/80 text-center text-sm text-gray-700 last:border-r-0"
      >
        <p className="mb-0 mt-1 text-xs font-semibold uppercase leading-3 text-gray-800">
          <span className="relative inline-block">
            {formatDate(date, "weekday")}
            {lockedForDay ? (
              <LockClosed
                className="absolute left-full top-1/2 ml-1 h-3.5 w-3.5 -translate-y-1/2 text-gray-700"
                aria-label="Locked day"
              />
            ) : null}
          </span>
        </p>
        <p className="mt-1 text-xs font-medium leading-3 text-gray-700">
          {datesOfWeekStrs[index]}
        </p>
      </div>
    );
  });

  // Stacked header matches DispatchContent (px-2) + SummaryBar (mx-1) so
  // weekday columns line up with the day cards in both 5- and 7-day views.
  if (layout === "stacked") {
    return (
      <div className="relative z-20 border-b border-gray-300 bg-gray-50">
        <div className="px-2">
          <div className={`grid gap-0 mx-1 ${gridCols}`}>{dateCells}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`absolute -left-2 z-20 grid w-[102%] gap-0 border-b border-gray-300 bg-gray-100 pl-2 ${gridCols} ${
        showWeekends ? "min-w-dispatchWeekends" : "min-w-dispatch"
      }`}
    >
      {dateCells}
    </div>
  );
};

export default DatesBar;
